import OutputFileUtil from "@/util/OutputFileUtil";
import { LoadingUtil } from "lib-vue-v2/util";

import { useMessageBox } from "lib-vue-v2/composables";
import { Resource as CommonResource } from "@/models/Message/Resource/CommonResource";

/// <summary>
/// カラムデータ
/// </summary>
export interface ExportColumnData {
    columnId: number;
    columnText: string;
}

/// <summary>
/// エクスポートオプション
/// </summary>
export interface ExportOptions {
    needHeader?: boolean;
    showCompleteMessage?: boolean;
}

// オプションのデフォルト値
const defaultExportOptions: Required<ExportOptions> = {
    needHeader: true,
    showCompleteMessage: false,
};

// メッセージボックス
const { showMessageBox } = useMessageBox();

export const executeExport = (fileName: string,
    indexs: Array<number>,
    columnsDatas: ExportColumnData[],
    filedDataFunction: (index: number, columnId: number) => [boolean, string],
    options?: ExportOptions) => {
    // デフォルト値と指定されたオプションをマージ
    const mergedOptions = { ...defaultExportOptions, ...options };
    try {
        LoadingUtil.Start();

        // 出力するものがない
        if (indexs.length === 0) {
            showMessageBox(CommonResource.CommonResource.getInstance().getMsgInfo(CommonResource.MsgID.NotIncludeDataExportError));
            return;
        }

        let datas = [];
        // createHeader
        if (mergedOptions.needHeader) {
            datas.push(createHeader(columnsDatas));
        }

        // createBody
        indexs.forEach((index) => {
            const rowResult = createBodyRow(
                index,
                columnsDatas,
                filedDataFunction
            );
            if (!rowResult[0]) return;// 念のため
            datas.push(rowResult[1]);
        });

        const data = datas.join("\r\n");
        if (mergedOptions.showCompleteMessage) {
            OutputFileUtil.saveFileWithEvent(fileName, data,
                () => {
                    showMessageBox(CommonResource.CommonResource.getInstance().getMsgInfo(CommonResource.MsgID.Export_Complete));
                },
                undefined,
                () => {
                    showMessageBox(CommonResource.CommonResource.getInstance().getMsgInfo(CommonResource.MsgID.Export_Failed));
                })
        }
        else OutputFileUtil.OutputFile(fileName, data);
    } catch {
        // 失敗
        showMessageBox(CommonResource.CommonResource.getInstance().getMsgInfo(CommonResource.MsgID.Export_Failed));
    } finally {
        LoadingUtil.Stop();
    }
}

export const executeExport2D = (fileName: string,
    indexs: number[][],
    columnsDatas: ExportColumnData[],
    filedDataFunction: (parentIndex: number, childIndex: number, columnId: number) => [boolean, string],
    options?: ExportOptions) => {
    // デフォルト値と指定されたオプションをマージ
    const mergedOptions = { ...defaultExportOptions, ...options };
    try {
        LoadingUtil.Start();

        // 出力するものがない
        const totalElements = indexs.reduce((acc, currentArray) => acc + currentArray.length, 0);
        if (totalElements === 0) {
            showMessageBox(CommonResource.CommonResource.getInstance().getMsgInfo(CommonResource.MsgID.NotIncludeDataExportError));
            return;
        }

        let datas = [];
        // createHeader
        if (mergedOptions.needHeader) {
            datas.push(createHeader(columnsDatas));
        }

        // createBody
        indexs.forEach((row, parentIndex) => {
            row.forEach((value, childIndex) => {
                const rowResult = createBodyRow2D(
                    parentIndex,
                    childIndex,
                    columnsDatas,
                    filedDataFunction
                );
                if (!rowResult[0]) return;// 念のため
                datas.push(rowResult[1]);
            });
        });

        const data = datas.join("\r\n");
        if (mergedOptions.showCompleteMessage) {
            OutputFileUtil.saveFileWithEvent(fileName, data,
                () => {
                    showMessageBox(CommonResource.CommonResource.getInstance().getMsgInfo(CommonResource.MsgID.Export_Complete));
                },
                undefined,
                () => {
                    showMessageBox(CommonResource.CommonResource.getInstance().getMsgInfo(CommonResource.MsgID.Export_Failed));
                })
        }
        else OutputFileUtil.OutputFile(fileName, data);
    } catch {
        // 失敗
        showMessageBox(CommonResource.CommonResource.getInstance().getMsgInfo(CommonResource.MsgID.Export_Failed));
    } finally {
        LoadingUtil.Stop();
    }
}

// ヘッダ行の作成
const createHeader = (columnsDatas: ExportColumnData[]): string => {
    const colums = columnsDatas.map((columnsData) =>
        escape(columnsData.columnText)
    );
    return colums.join(",");
};

// ボディ行の作成
const createBodyRow = (
    index: number,
    columnsDatas: ExportColumnData[],
    filedDataFunction: (index: number, columnId: number) => [boolean, string]
): [boolean, string] => {
    const fields = columnsDatas.map((columnsData) =>
        filedDataFunction(index, columnsData.columnId)
    );
    // 一つでも失敗があれば失敗に倒す
    if (fields.some((field) => !field[0])) {
        return [false, ""];
    }
    // データ部のエスケープを行う
    let bodyRows = fields.map((field) => escape(field[1]));
    return [true, bodyRows.join(",")];
};

const createBodyRow2D = (
    parentIndex: number,
    childIndex: number,
    columnsDatas: ExportColumnData[],
    filedDataFunction: (parentIndex: number, childIndex: number, columnId: number) => [boolean, string]
): [boolean, string] => {
    const fields = columnsDatas.map((columnsData) =>
        filedDataFunction(parentIndex, childIndex, columnsData.columnId)
    );
    // 一つでも失敗があれば失敗に倒す
    if (fields.some((field) => !field[0])) {
        return [false, ""];
    }
    // データ部のエスケープを行う
    let bodyRows = fields.map((field) => escape(field[1]));
    return [true, bodyRows.join(",")];
};

// エスケープが必要ならエスケープを行うように
const escape = (value: string): string => {
    if (value.includes('"')) {
        value = value.replace(/"/g, '""');
    }
    if (value.includes(",") || value.includes("\n") || value.includes('"')) {
        value = `"${value}"`;
    }
    return value;
};